import React, { useState, useEffect, useRef, useId } from 'react';
import { Tag, TagType } from '@platform/witems';
import { usePatientArchived } from '../state/data/patient';
import useWithdrawalHold from '../hooks/useWithdrawalHold';
import { Section, Callout } from './cms/cmsModalParts';
import { WITHDRAWAL_REASON_CODES, encodeRequestReason } from '../logic/withdrawalReason';
import {
  lockInEnd, isLockInOver, formatLockInDate, withdrawalRequestOf, holdsOnPeriod,
  chartsMismatch, requestCarePeriodId, requestedOnLabel, WITHDRAWAL_REQUEST_ON,
} from '../logic/withdrawalRequest';

const FOOTER_STYLE = {
  display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center',
  padding: '14px 24px', borderTop: '1px solid var(--wi-divider)', flexShrink: 0,
};
const BODY_STYLE = { flex: 1, overflowY: 'auto', padding: '20px 24px' };

// What a hold stops and what still goes on, as the workflows' gates decide (spec 2026-10-26 §10).
const MESSAGES_STOPPED = 'Automated messages to the patient';
const CHARTS = 'New readings in the patient file and the charts';
const ALERTS_STOPPED = 'Alerts and tasks from readings, even a crisis-level blood pressure or a critical lab value';
const STILL_ON = [
  'The patient’s enrollment with CMS',
  'The 911 reply to an emergency in the chat or a red check-in',
  'Messages the care team sends',
];

function EffectList({ title, items, mark, color }) {
  const titleId = useId();
  return (
    <div>
      <p id={titleId} className="detail1" style={{ margin: 0, fontWeight: 600, color: 'var(--wi-text-secondary)' }}>{title}</p>
      <ul aria-labelledby={titleId} style={{ listStyle: 'none', margin: '4px 0 0', padding: 0 }}>
        {items.map((text) => (
          <li key={text} className="body2" style={{ display: 'flex', gap: 8, lineHeight: 1.5, color: 'var(--wi-text-primary)' }}>
            <span aria-hidden="true" style={{ color, fontWeight: 600, flexShrink: 0 }}>{mark}</span>
            <span>{text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

// The charts stop only once the patient is archived: while they are live, say so rather than list them as stopped.
function HoldEffects({ chartsLive = false }) {
  const stopped = chartsLive ? [MESSAGES_STOPPED, ALERTS_STOPPED] : [MESSAGES_STOPPED, CHARTS, ALERTS_STOPPED];
  const stillOn = chartsLive ? [CHARTS, ...STILL_ON] : STILL_ON;
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <EffectList title="Stopped" items={stopped} mark="✕" color="var(--wi-text-primary-bad)" />
      <EffectList title="Still on" items={stillOn} mark="✓" color="var(--wi-text-primary-good)" />
    </div>
  );
}

function Facts({ rows }) {
  return (
    <dl style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 20, rowGap: 8, margin: 0 }}>
      {rows.map(([label, value, note]) => (
        <React.Fragment key={label}>
          <dt className="detail1" style={{ color: 'var(--wi-text-secondary)', paddingTop: 2 }}>{label}</dt>
          <dd className="body2" style={{ margin: 0, color: 'var(--wi-text-primary)' }}>
            {value}
            {note && <span className="detail1" style={{ display: 'block', color: 'var(--wi-text-secondary)' }}>{note}</span>}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

const CANCEL_ERRORS = {
  not_sent: 'The request could not be cancelled.',
  not_unarchived: 'Couldn’t restart the charts, so nothing was cancelled. Try again.',
  cancel_failed: 'The charts restarted, but the cancel was not sent. Try again.',
  not_confirmed: 'The cancel was sent but is not confirmed yet. Check again in a minute.',
};

// The withdrawal request half of the Withdraw window: CMS still holds the patient (aligned, or an
// unalignment it has not answered), so the window records a coordinator task instead of withdrawing,
// and archives the patient so the charts stop too. Spec: docs/specs/2026-10-26-withdrawal-request-contact-hold.md §10.
export default function WithdrawalRequestPanel({
  patient, carePeriod, requestQuery, requestOn = WITHDRAWAL_REQUEST_ON, isCoordinator, actor, onSaved, onRequestUnalign, onClose,
}) {
  const [reasonCode, setReasonCode] = useState('');
  const [phase, setPhase] = useState('idle');
  const [notRecorded, setNotRecorded] = useState(false);
  const [archiveFailed, setArchiveFailed] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [chartsError, setChartsError] = useState(null);
  const [confirmStop, setConfirmStop] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  // One clock read per open, like SnoozeTaskModal: the window must not change its verdict under the user.
  const [nowMs] = useState(() => Date.now());
  const aliveRef = useRef(true);
  const uid = String(patient.userid ?? '');
  const hold = useWithdrawalHold(uid);
  const archivedQuery = usePatientArchived(uid);
  // A category being re-read is not known yet: the window's own archive / un-archive must not flash the banner.
  const archived = archivedQuery.isFetching ? null : (archivedQuery.data ?? null);

  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const unaligning = carePeriod?.status === 'pending_unalignment';
  const request = requestQuery.data ?? null;
  const requestView = withdrawalRequestOf(request);
  const pausing = holdsOnPeriod(request, carePeriod);
  const requestPeriod = request ? requestCarePeriodId(request) : null;
  const otherPeriod = !!requestPeriod && requestPeriod !== String(carePeriod?.care_period_id ?? '');
  // Once CMS is unaligning, the request runs to the withdrawal: a cancel would resume a patient CMS is releasing.
  const canCancel = carePeriod?.status === 'active';
  const lockIn = otherPeriod ? null : lockInEnd(carePeriod, nowMs);
  const unalignNow = !unaligning && !otherPeriod
    && (isLockInOver(lockIn, nowMs) || requestView?.reasonCode === 'clinically_excluded');
  const lockInLine = lockIn ? `Unalignment possible from ${formatLockInDate(lockIn.ymd)}.` : 'Alignment date unknown.';
  const busy = phase !== 'idle' || hold.busy;
  const mismatch = busy ? null : (chartsMismatch(pausing, archived) ?? (archiveFailed && pausing ? 'stop' : null));

  async function handleSave() {
    const encoded = encodeRequestReason(reasonCode);
    if (!encoded || busy || carePeriod?.care_period_id == null) return;
    setNotRecorded(false);
    setPhase('saving');
    const outcome = await hold.save(String(carePeriod.care_period_id), encoded, actor);
    if (!aliveRef.current) return;
    setPhase('idle');
    if (outcome === 'not_recorded') { setNotRecorded(true); return; }
    setArchiveFailed(outcome === 'saved_not_archived');
    onSaved?.();
  }

  async function handleCancel() {
    if (!request || busy || !canCancel) return;
    setCancelError(null);
    setPhase('cancelling');
    const outcome = await hold.cancel(request, actor);
    if (!aliveRef.current) return;
    setPhase('idle');
    if (outcome === 'cancelled') {
      setConfirmCancel(false);
      setArchiveFailed(false);
      setCancelled(true);
      onSaved?.();
      return;
    }
    setCancelError(CANCEL_ERRORS[outcome] || CANCEL_ERRORS.not_sent);
  }

  async function handleCharts() {
    if (busy || !mismatch) return;
    // Stopping the charts loses every reading taken meanwhile, and a request can come from outside the app.
    if (mismatch === 'stop' && !confirmStop) { setConfirmStop(true); return; }
    setConfirmStop(false);
    setChartsError(null);
    setPhase('charts');
    const ok = mismatch === 'stop' ? await hold.stopCharts() : await hold.resumeCharts();
    if (!aliveRef.current) return;
    setPhase('idle');
    if (ok) setArchiveFailed(false);
    else setChartsError(mismatch === 'stop' ? 'Couldn’t stop the charts. Try again.' : 'Couldn’t restart the charts. Try again.');
  }

  const chartsCallout = mismatch && (
    <Callout tone={mismatch === 'stop' ? 'bad' : 'moderate'}>
      <p className="body2" style={{ margin: 0, color: mismatch === 'stop' ? 'var(--wi-text-primary-bad)' : 'var(--wi-text-primary-moderate)' }}>
        {mismatch === 'stop'
          ? 'New readings still reach the charts: the request is open, but the patient is not archived in the RPM program.'
          : `The patient is archived in the RPM program, so new readings don’t reach the charts, but ${request ? 'this request holds nothing' : 'no withdrawal request is open'}.`}
      </p>
      {confirmStop && mismatch === 'stop' && (
        <p className="body2" style={{ margin: '6px 0 0', color: 'var(--wi-text-primary-bad)' }}>
          Stop the charts? Readings taken until the request ends never come back to them. Do this only for a request you know is genuine: the care-team Chat line names who saved it.
        </p>
      )}
      {chartsError && (
        <p className="detail1" style={{ margin: '6px 0 0', color: mismatch === 'stop' ? 'var(--wi-text-primary-bad)' : 'var(--wi-text-primary-moderate)' }}>{chartsError}</p>
      )}
      {isCoordinator && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
          {confirmStop && mismatch === 'stop' && (
            <button className="btn-secondary" onClick={() => setConfirmStop(false)} disabled={busy}>Keep them live</button>
          )}
          <button className="btn-secondary" onClick={handleCharts} disabled={busy}>
            {phase === 'charts' ? 'Updating…' : (mismatch === 'stop' ? 'Stop the charts' : 'Resume the charts')}
          </button>
        </div>
      )}
    </Callout>
  );

  let body;
  let actions = null;
  if (requestQuery.isLoading) {
    body = <p className="body2" style={{ margin: 0, color: 'var(--wi-text-secondary)' }}>Checking for an existing request…</p>;
  } else if (requestQuery.isError && !request) {
    body = (
      <Callout tone="bad">
        <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-bad)' }}>Couldn’t check for an existing request.</p>
      </Callout>
    );
    actions = <button className="btn-primary" onClick={() => requestQuery.refetch()}>Retry</button>;
  } else if (phase === 'saving') {
    body = <p className="body2" style={{ margin: 0, color: 'var(--wi-text-secondary)' }}>Recording the request…</p>;
  } else if (request) {
    const requestedOn = requestedOnLabel(request);
    let unalignRow = null;
    if (otherPeriod) unalignRow = ['Care period', 'Saved on another care period'];
    else if (!unaligning && unalignNow) unalignRow = ['CMS unalignment', 'Allowed now'];
    else if (!unaligning && lockIn) unalignRow = ['CMS unalignment', `Possible from ${formatLockInDate(lockIn.ymd)}`, 'A coordinator task is due that day.'];
    else if (!unaligning) unalignRow = ['CMS unalignment', 'Date unknown'];
    const factRows = [['Reason', requestView?.reasonLabel ?? 'Reason not recorded'], unalignRow].filter(Boolean);
    body = (
      <div>
        {unaligning && (
          <Callout tone="bad">
            <p className="body2" style={{ margin: 0, fontWeight: 600, color: 'var(--wi-text-primary-bad)' }}>CMS unalignment is still in progress</p>
            <p className="body2" style={{ margin: '6px 0 0', color: 'var(--wi-text-primary-bad)' }}>
              The withdrawal unlocks once CMS returns its verdict.
            </p>
          </Callout>
        )}
        {chartsCallout}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: unaligning || chartsCallout ? 18 : 0 }}>
          <Tag type={TagType.MODERATE} label="Withdrawal requested" />
          {requestedOn && <span className="detail1" style={{ color: 'var(--wi-text-secondary)' }}>{requestedOn}</span>}
        </div>
        <div style={{ marginTop: 14 }}>
          <Facts rows={factRows} />
        </div>
        {pausing && (
          <Section label="While the request is open">
            <HoldEffects chartsLive={mismatch === 'stop'} />
          </Section>
        )}
        {confirmCancel && (
          <Callout tone="moderate">
            <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
              Cancel this request? The charts restart first, then automated messages, readings and alerts resume. Readings taken while the request was open don’t come back to the charts.
            </p>
          </Callout>
        )}
        {cancelError && <p className="detail1" style={{ margin: '12px 0 0', color: 'var(--wi-text-primary-bad)' }}>{cancelError}</p>}
      </div>
    );
    if (isCoordinator) {
      actions = confirmCancel ? (
        <>
          <button className="btn-secondary" onClick={() => setConfirmCancel(false)} disabled={busy}>Keep</button>
          <button className="btn-primary" onClick={handleCancel} disabled={busy}>
            {phase === 'cancelling' ? 'Cancelling…' : 'Cancel request'}
          </button>
        </>
      ) : (
        <>
          {canCancel && <button className="btn-secondary" onClick={() => setConfirmCancel(true)} disabled={busy}>Cancel the request</button>}
          {unalignNow && onRequestUnalign && (
            <button className="btn-primary" onClick={() => onRequestUnalign(requestView?.reasonLabel ?? null)}>Unalign in CMS</button>
          )}
        </>
      );
    }
  } else if (unaligning) {
    body = (
      <Callout tone="bad">
        <p className="body2" style={{ margin: 0, fontWeight: 600, color: 'var(--wi-text-primary-bad)' }}>CMS unalignment is still in progress</p>
        <p className="body2" style={{ margin: '6px 0 0', color: 'var(--wi-text-primary-bad)' }}>
          The withdrawal unlocks once CMS returns its verdict.
        </p>
      </Callout>
    );
  } else if (!requestOn) {
    body = cancelled ? (
      <Callout tone="good">
        <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-good)' }}>
          Request cancelled. Automated messages, readings and alerts resume for this patient.
        </p>
      </Callout>
    ) : <p className="body2" style={{ margin: 0, color: 'var(--wi-text-secondary)' }}>No withdrawal request is open.</p>;
  } else {
    body = (
      <div>
        {cancelled && (
          <Callout tone="good">
            <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-good)' }}>
              Request cancelled. Automated messages, readings and alerts resume for this patient.
            </p>
          </Callout>
        )}
        {chartsCallout}
        <Callout tone="moderate">
          <p className="body2" style={{ margin: 0, color: 'var(--wi-text-primary-moderate)' }}>
            {`This patient is aligned with CMS, which does not allow unalignment during the 90 days after alignment. ${lockInLine}`}
          </p>
        </Callout>
        <Section label="Why is this patient leaving?">
          <div className="rx-field">
            <label className="eyebrow" htmlFor="withdraw-request-reason">Reason (required)</label>
            <select
              id="withdraw-request-reason"
              className="rx-select"
              value={reasonCode}
              onChange={(e) => setReasonCode(e.target.value)}
              disabled={!isCoordinator}
            >
              <option value="">Select a reason…</option>
              {WITHDRAWAL_REASON_CODES.map(r => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
          </div>
        </Section>
        <Section label="What saving does">
          <p className="body2" style={{ margin: '0 0 10px', color: 'var(--wi-text-primary)' }}>
            The request and its reason are saved, and a coordinator task is due the day CMS allows the unalignment.
            Until the request is cancelled or the withdrawal is done:
          </p>
          <HoldEffects />
        </Section>
        {notRecorded && (
          <p className="detail1" style={{ margin: '12px 0 0', color: 'var(--wi-text-primary-bad)' }}>The request was not recorded yet.</p>
        )}
      </div>
    );
    if (isCoordinator) {
      actions = (
        <button className="btn-primary" onClick={handleSave} disabled={!reasonCode || busy}>
          {notRecorded ? 'Try again' : 'Save withdrawal request'}
        </button>
      );
    }
  }

  return (
    <>
      <div style={BODY_STYLE}>{body}</div>
      <div style={FOOTER_STYLE}>
        <button className="btn-secondary" onClick={onClose}>Close</button>
        {actions}
      </div>
    </>
  );
}
